import { useEffect, useState } from "react";
import styles from "./GeneratingOverlay.module.scss";

interface GeneratingOverlayProps {
  isVisible: boolean;
  estimatedSeconds?: number;
  message?: string;
  displayTime?: boolean;
}

const loadingMessages = [
  "Crafting your unique skin...",
  "Mining for the perfect pixels...",
  "Placing blocks with care...",
  "Adding the finishing touches...",
  "Almost ready to play!",
  "Painting your character...",
  "Polishing the details...",
  "Making it shine...",
];

const GeneratingOverlay = ({
  isVisible,
  estimatedSeconds = 30,
  message,
  displayTime = true,
}: GeneratingOverlayProps) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setElapsed(0);
      setCurrentMessageIndex(0);
      return;
    }

    // Update elapsed time every second
    const timer = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    // Rotate messages every 4 seconds
    const messageTimer = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 4000);

    return () => {
      clearInterval(timer);
      clearInterval(messageTimer);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  // Cap progress at 95% until complete to avoid misleading users
  const progress = Math.min((elapsed / estimatedSeconds) * 100, 95);
  const remaining = Math.max(Math.ceil(estimatedSeconds - elapsed), 0);

  return (
    <div className={styles.overlay}>
      <div className={styles.content}>
        {/* Animated Minecraft block */}
        <div className={styles.blockContainer}>
          <div className={styles.block}>
            <div className={styles.blockFace + " " + styles.front}></div>
            <div className={styles.blockFace + " " + styles.back}></div>
            <div className={styles.blockFace + " " + styles.right}></div>
            <div className={styles.blockFace + " " + styles.left}></div>
            <div className={styles.blockFace + " " + styles.top}></div>
            <div className={styles.blockFace + " " + styles.bottom}></div>
          </div>
        </div>

        {/* Loading message */}
        <p className={styles.message}>
          {message ?? loadingMessages[currentMessageIndex]}
        </p>

        {/* Progress bar */}
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>

          {displayTime && (
            <p className={styles.timeEstimate}>
              {remaining > 0
                ? `${remaining} seconds remaining`
                : "Just a moment more..."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratingOverlay;
